import { useMemo } from "react";

import { t } from "../../../i18n";
import { cn } from "../../../lib/cn";
import { formatDate } from "../../../lib/format";
import { trpc } from "../../../lib/trpc";
import { Icon, type IconName } from "../../ui";
import type { Member, Settlement, SettlementEvent } from "../types";

// Static visual styling per event type. Defined outside the component so
// the object identity is stable across renders.
const HISTORY_STYLES: Record<
  SettlementEvent["eventType"] | "settlement.created",
  { icon: IconName; iconClass: string; bgClass: string }
> = {
  "settlement.created": {
    icon: "sparkles",
    iconClass: "text-accent-700 dark:text-accent-200",
    bgClass: "bg-accent-100 dark:bg-accent-900/40",
  },
  "settlement.approved": {
    icon: "check-circle",
    iconClass: "text-emerald-700 dark:text-emerald-200",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/40",
  },
  "settlement.unapproved": {
    icon: "alert-circle",
    iconClass: "text-amber-700 dark:text-amber-200",
    bgClass: "bg-amber-100 dark:bg-amber-900/40",
  },
  "settlement.locked": {
    icon: "lock",
    iconClass: "text-emerald-700 dark:text-emerald-200",
    bgClass: "bg-emerald-100 dark:bg-emerald-900/40",
  },
  "settlement.reopened": {
    icon: "unlock",
    iconClass: "text-accent-700 dark:text-accent-200",
    bgClass: "bg-accent-100 dark:bg-accent-900/40",
  },
};

interface SettlementHistoryProps {
  settlementId: string;
  createdAt: string;
  createdBy: string;
  memberApprovals: Settlement["memberApprovals"];
  lockedAt: string | null;
  members: Member[];
  myUserId: string | null;
}

/**
 * Settlement history timeline. Renders a vertical list of state-change
 * entries with icon + actor + timestamp.
 *
 * Two sources of truth, layered:
 *   1. Real events from the `events` table (settlement.approved /
 *      unapproved / locked / reopened) — authoritative when present.
 *   2. State-derived synthesised entries for settlements that pre-date
 *      this feature. We fall back to `settlement_approvals.approved_at`
 *      for approve entries and `settlement.locked_at` for the lock
 *      entry, but only when no equivalent real event already covers
 *      that user / state transition. Without this, old already-locked
 *      settlements would show only "Du opprettet oppgjøret" even
 *      though everyone had approved and the settlement was settled.
 *
 * Limitations: pre-feature unapprove and reopen events can't be
 * reconstructed (we only have current state, not transitions), and a
 * synthesised lock entry has no actor — we use a passive form
 * ("Oppgjøret ble gjort opp") for it.
 */
export function SettlementHistory({
  settlementId,
  createdAt,
  createdBy,
  memberApprovals,
  lockedAt,
  members,
  myUserId,
}: SettlementHistoryProps) {
  const eventsQuery = trpc.settlement.events.useQuery({ settlementId });
  const memberMap = useMemo(() => new Map(members.map((m) => [m.userId, m])), [members]);

  const resolveActorLabel = (userId: string | null, fallback: string | null): string => {
    if (userId && userId === myUserId) return t.settlement.summary.history.you;
    if (userId) {
      const member = memberMap.get(userId);
      const label = member?.displayName ?? member?.email ?? fallback;
      if (label) return label;
    }
    return fallback ?? t.settlement.summary.history.unknownActor;
  };

  type Entry = {
    key: string;
    label: string;
    timestamp: string;
    style: (typeof HISTORY_STYLES)[keyof typeof HISTORY_STYLES];
  };

  const entries: Entry[] = [];

  // Synthesised "created" entry — always first, sourced from settlement
  // metadata so it's available even before any real event is persisted.
  entries.push({
    key: `created:${settlementId}`,
    label: t.settlement.summary.history.created(resolveActorLabel(createdBy, null)),
    timestamp: createdAt,
    style: HISTORY_STYLES["settlement.created"],
  });

  // Dedup keys for state-derived fallback entries. If a real event
  // already covers a transition we never synthesise the same entry
  // again, so feature-era settlements show only the canonical record.
  const realEvents = eventsQuery.data ?? [];
  const usersWithApproveEvent = new Set<string>();
  let hasLockEvent = false;
  for (const ev of realEvents) {
    if (ev.eventType === "settlement.approved" && ev.actorUserId) {
      usersWithApproveEvent.add(ev.actorUserId);
    } else if (ev.eventType === "settlement.locked") {
      hasLockEvent = true;
    }
  }

  // Synthesised approvals from settlement_approvals for members who
  // approved before we started recording events. We keep the real
  // event when both exist (it carries the canonical `created_at`).
  for (const approval of memberApprovals) {
    if (!approval.approvedAt) continue;
    if (usersWithApproveEvent.has(approval.userId)) continue;
    const isSelf = approval.userId === myUserId;
    const actor = resolveActorLabel(approval.userId, null);
    entries.push({
      key: `approved-state:${approval.userId}`,
      label: isSelf
        ? t.settlement.summary.history.approvedSelf
        : t.settlement.summary.history.approvedOther(actor),
      timestamp: approval.approvedAt,
      style: HISTORY_STYLES["settlement.approved"],
    });
  }

  // Synthesised lock for old settlements: we know it was locked and
  // when, but not by whom — use the passive-voice copy.
  if (lockedAt && !hasLockEvent) {
    entries.push({
      key: `locked-state:${settlementId}`,
      label: t.settlement.summary.history.lockedUnknownActor,
      timestamp: lockedAt,
      style: HISTORY_STYLES["settlement.locked"],
    });
  }

  for (const event of realEvents) {
    const isSelf = !!event.actorUserId && event.actorUserId === myUserId;
    const actor = resolveActorLabel(event.actorUserId, event.actorDisplayName ?? event.actorEmail);
    const label = (() => {
      switch (event.eventType) {
        case "settlement.approved":
          return isSelf
            ? t.settlement.summary.history.approvedSelf
            : t.settlement.summary.history.approvedOther(actor);
        case "settlement.unapproved":
          return isSelf
            ? t.settlement.summary.history.unapprovedSelf
            : t.settlement.summary.history.unapprovedOther(actor);
        case "settlement.locked":
          return t.settlement.summary.history.locked(actor);
        case "settlement.reopened":
          return t.settlement.summary.history.reopened(actor);
      }
    })();
    entries.push({
      key: `evt:${event.id}`,
      label,
      timestamp: event.createdAt,
      style: HISTORY_STYLES[event.eventType],
    });
  }

  // Oldest first so the timeline reads chronologically top-to-bottom
  // ("Opprettet" → "godkjente" → "gjort opp"). Sort is required because
  // synthesised entries and real events are appended in two passes.
  entries.sort((a, b) => a.timestamp.localeCompare(b.timestamp));

  return (
    <div>
      <h4 className="mb-3 flex items-center gap-2 font-medium text-ink-700 text-sm">
        <Icon name="rows-3" size="sm" className="text-ink-500" />
        {t.settlement.summary.history.heading}
      </h4>
      {eventsQuery.isPending && !eventsQuery.data ? (
        <p className="text-ink-500 text-sm">{t.settlement.summary.history.loading}</p>
      ) : entries.length === 0 ? (
        <p className="text-ink-500 text-sm">{t.settlement.summary.history.empty}</p>
      ) : (
        // Each row has a fixed-width left column with the icon circle and a
        // connector line that fills the rest of the column down to the next
        // icon. Drawing the line per-row (instead of one absolute line
        // behind the list) means it always stops exactly at the last item
        // and never sticks out below.
        <ol>
          {entries.map((entry, idx) => {
            const isLast = idx === entries.length - 1;
            return (
              <li key={entry.key} className="flex items-stretch gap-3">
                <div className="flex w-7 shrink-0 flex-col items-center">
                  <span
                    aria-hidden="true"
                    className={cn(
                      "flex h-7 w-7 shrink-0 items-center justify-center rounded-full",
                      entry.style.bgClass,
                    )}
                  >
                    <Icon name={entry.style.icon} size="xs" className={entry.style.iconClass} />
                  </span>
                  {!isLast && (
                    <span
                      aria-hidden="true"
                      className="my-1 w-px flex-1 bg-ink-200 dark:bg-ink-800"
                    />
                  )}
                </div>
                <div className={cn("min-w-0 flex-1 pt-0.5", !isLast && "pb-4")}>
                  <p className="text-ink-800 text-sm leading-snug">{entry.label}</p>
                  <p className="tabular mt-0.5 text-ink-500 text-xs">
                    {formatDate(entry.timestamp)}
                  </p>
                </div>
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}
